import { defineStore } from 'pinia'
import { ref } from 'vue'
import { AI_PANEL_STORAGE_KEY } from '@/config/ai.config'

export const PANEL_MIN_WIDTH = 360
export const PANEL_DEFAULT_WIDTH = 420
export const PANEL_MAX_WIDTH = 720

/** Viewport-aware upper bound — also applied when restoring a persisted width. */
function maxForViewport(): number {
  return Math.min(PANEL_MAX_WIDTH, Math.round(window.innerWidth * 0.5))
}

/**
 * DEV-7125 — persisted AI assistant panel state: drawer open flag, width
 * (viewport-clamped), expand toggle, transient resize flag, and the per-screen
 * `route name → last sessionId` map behind "load the last chat from that screen".
 * Cleared on logout via `AI_PANEL_STORAGE_KEY` (auth.store).
 */
export const useAiPanelStore = defineStore(
  'aiPanel',
  () => {
    const isOpen = ref(false)
    const width = ref(PANEL_DEFAULT_WIDTH)
    const isExpanded = ref(false)
    /** Transient — drives the .ai-panel-resizing transition-disable class (main.scss). */
    const isResizing = ref(false)
    /** route name → last real sessionId used on that screen */
    const screenToSession = ref<Record<string, string>>({})

    function open(): void {
      clampWidth()
      isOpen.value = true
    }
    function close(): void {
      isOpen.value = false
    }
    function setWidth(px: number): void {
      width.value = Math.round(Math.min(maxForViewport(), Math.max(PANEL_MIN_WIDTH, px)))
      isExpanded.value = width.value >= maxForViewport()
    }
    /** Re-clamp a persisted width against the current viewport (open AND drawer-mount time). */
    function clampWidth(): void {
      setWidth(width.value)
    }
    function toggleExpand(): void {
      setWidth(isExpanded.value ? PANEL_DEFAULT_WIDTH : maxForViewport())
    }
    function setResizing(v: boolean): void {
      isResizing.value = v
    }
    function recordScreenSession(screen: string, sessionId: string): void {
      if (!screen || !sessionId || sessionId === 'placeholder') return
      screenToSession.value = { ...screenToSession.value, [screen]: sessionId }
    }
    function forgetSession(sessionId: string): void {
      screenToSession.value = Object.fromEntries(
        Object.entries(screenToSession.value).filter(([, v]) => v !== sessionId),
      )
    }
    function $reset(): void {
      isOpen.value = false
      width.value = PANEL_DEFAULT_WIDTH
      isExpanded.value = false
      isResizing.value = false
      screenToSession.value = {}
    }

    return {
      isOpen,
      width,
      isExpanded,
      isResizing,
      screenToSession,
      open,
      close,
      setWidth,
      clampWidth,
      toggleExpand,
      setResizing,
      recordScreenSession,
      forgetSession,
      $reset,
    }
  },
  {
    persist: {
      key: AI_PANEL_STORAGE_KEY,
      pick: ['isOpen', 'width', 'isExpanded', 'screenToSession'],
    },
  },
)
